import React from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { TestRun } from "../../model_gen/evaluation";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import { 
  MoreHorizontalIcon,
  TrashIcon,
  Loader2Icon,
  GitCompareArrows,
  PlusIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";

interface TestRunsTableProps {
  testRuns: TestRun[];
  loading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalTestRuns: number;
  testRunProgress: Record<string, any>;
  isCompareMode: boolean;
  selectedTestRunsMap: Record<number, boolean>;
  onTestRunSelect: (testRunId: number, checked: boolean) => void;
  onDeleteTestRun: (testRun: TestRun) => void;
  onNewTestRun: () => void;
  onCompareClick: () => void;
  onCancelCompare: () => void;
  onCompareRuns: () => void;
  selectedTestRunIds: number[];
  testSetId: string;
  intentGroupId: number;
}

const TestRunsTable: React.FC<TestRunsTableProps> = ({
  testRuns,
  loading,
  currentPage,
  onPageChange,
  totalTestRuns,
  testRunProgress,
  isCompareMode,
  selectedTestRunsMap,
  onTestRunSelect,
  onDeleteTestRun,
  onNewTestRun,
  onCompareClick,
  onCancelCompare,
  onCompareRuns,
  selectedTestRunIds,
  testSetId,
  intentGroupId,
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      "CREATED": "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
      "COMPLETED": "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
      "ERROR": "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
      "PENDING": "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
      "RUNNING": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
    };
    return colors[status] || "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
  };

  const getPassRateColor = (passRate: number) => {
    if (passRate >= 80) return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
    if (passRate >= 60) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
    return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
  };

  const columns: PaginatedTableColumn<TestRun>[] = [
    ...(isCompareMode ? [{
      key: "select" as keyof TestRun,
      header: "Select",
      width: "w-[5%]",
      render: (testRun: TestRun) => {
        const progressData = testRunProgress[testRun.id.toString()];
        const status = progressData?.status || testRun.status;
        const isDisabled = status === "CREATED" || status === "PENDING" || status === "RUNNING";
        
        return (
          <Checkbox
            checked={selectedTestRunsMap[testRun.id] || false}
            disabled={isDisabled}
            onCheckedChange={(checked) => onTestRunSelect(testRun.id, !!checked)}
          />
        );
      }
    }] : []),
    {
      key: "date_created" as keyof TestRun,
      header: "Date Created",
      width: "w-[15%]",
      render: (testRun: TestRun) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(testRun.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(testRun.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "description" as keyof TestRun,
      header: "Description",
      width: "w-[30%]",
      render: (testRun: TestRun) => (
        <span className="text-sm font-gc-reg text-foreground">
          {testRun.description || "-"}
        </span>
      )
    },
    {
      key: "passRate" as keyof TestRun,
      header: "Accuracy",
      width: "w-[20%]",
      render: (testRun: TestRun) => {
        const summary = testRun.qualitative_evaluation_summary;
        if (!summary || !summary.completed_requests) {
          return <span className="text-sm text-muted-foreground">-</span>;
        }

        if (summary.exact_match_count == null) {
          return <span className="text-sm text-muted-foreground">-</span>;
        }

        const accuracy = (summary.exact_match_count / summary.completed_requests) * 100;

        return (
          <Badge className={cn("font-gc-reg", getPassRateColor(accuracy))}>
            {accuracy.toFixed(1)}%
          </Badge>
        );
      }
    },
    {
      key: "avgTime" as keyof TestRun,
      header: "Avg Time",
      width: "w-[15%]",
      render: (testRun: TestRun) => {
        const summary = testRun.qualitative_evaluation_summary;
        if (!summary?.response_time_percentiles?.p50) {
          return <span className="text-sm text-muted-foreground">-</span>;
        }
        return (
          <span className="text-sm font-gc-reg text-foreground">
            {Math.round(summary.response_time_percentiles.p50)}ms
          </span>
        );
      }
    },
    {
      key: "status" as keyof TestRun,
      header: "Status",
      width: "w-[15%]",
      render: (testRun: TestRun) => {
        const progressData = testRunProgress[testRun.id!.toString()];
        const status = progressData?.status || testRun.status;
        
        if (status === "RUNNING" || status === "PENDING" || status === "CREATED") {
          const total = progressData?.total_requests || 0;
          const completed = (progressData?.completed_requests || 0) + (progressData?.error_requests || 0);
          const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

          return (
            <div className="space-y-1.5 min-w-[100px]">
              <div className="flex items-center gap-2">
                <Loader2Icon className="w-3.5 h-3.5 animate-spin text-maitai-green flex-shrink-0" />
                <span className="text-xs font-gc-med text-muted-foreground">
                  {total > 0 ? `${completed}/${total}` : "Starting..."}
                </span>
              </div>
              {total > 0 && (
                <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-maitai-green transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              )}
            </div>
          );
        }

        return (
          <Badge className={cn("font-gc-reg", getStatusColor(status))}>
            {status}
          </Badge>
        );
      }
    },
    {
      key: "actions" as keyof TestRun,
      header: "Actions",
      width: "w-[5%]",
      render: (testRun: TestRun) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontalIcon className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem 
                  className="text-red-600 focus:text-red-600"
                  onSelect={(e) => e.preventDefault()}
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Test Run</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this test run? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDeleteTestRun(testRun)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  const handleRowClick = (testRun: TestRun) => {
    if (isCompareMode) return;
    
    const progressData = testRunProgress[testRun.id.toString()];
    const status = progressData?.status || testRun.status;
    const isDisabled = status === "CREATED" || status === "PENDING" || status === "RUNNING";
    
    if (!isDisabled) {
      navigate(`/intent-group/${intentGroupId}/testsets/${testSetId}/runs/${testRun.id}`);
    }
  };

  const getRowClassName = (testRun: TestRun) => {
    const progressData = testRunProgress[testRun.id.toString()];
    const status = progressData?.status || testRun.status;
    const isDisabled = status === "CREATED" || status === "PENDING" || status === "RUNNING";
    
    return cn(
      "transition-colors",
      !isCompareMode && !isDisabled && "cursor-pointer hover:bg-accent/20",
      isDisabled && "opacity-75"
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Test Runs</h3>
          <p className="text-sm text-muted-foreground">Manage and compare test run results</p>
        </div>
        <div className="flex items-center gap-3">
          {!isCompareMode ? (
            <>
              <Button variant="outline" onClick={onCompareClick} className="gap-2">
                <GitCompareArrows className="w-4 h-4" />
                Compare Runs
              </Button>
              <Button onClick={onNewTestRun} className="gap-2">
                <PlusIcon className="w-4 h-4" />
                New Test Run
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onCancelCompare}>
                Cancel
              </Button>
              <Button
                onClick={onCompareRuns}
                disabled={selectedTestRunIds.length < 2}
                className="gap-2"
              >
                Compare{" "}
                {selectedTestRunIds.length > 0
                  ? `(${selectedTestRunIds.length})`
                  : ""}
              </Button>
            </>
          )}
        </div>
      </div>

      <PaginatedTable
        data={testRuns}
        columns={columns}
        loading={loading}
        currentPage={currentPage}
        onPageChange={onPageChange}
        pageSize={5}
        totalItems={totalTestRuns}
        serverSidePagination={true}
        emptyTitle="No test runs found"
        emptyDescription="Create a new test run to get started."
        onRowClick={handleRowClick}
        rowClassName={getRowClassName}
        loadingRows={5}
      />
    </div>
  );
};

export default TestRunsTable; 